import { BackendObjectRef } from './backend-integration.service';
import * as Utils from './utils';

export interface TransitionActionData {
    [key: string]: string|number|undefined|TransitionActionData;
}

export interface TransitionActionOption {
    id: string;
    name: string;
    type: 'text'|'number'|'boolean'|'wysiwyg';
    default?: boolean;
}

/**
 * Possible data sources for select options,
 * see evaluateOptionCategoriesFromDataSource
 */
export type TransitionActionSelectOptionDataSource = 'availableRoles';

/**
 * Select option for TransitionAction
 * 
 * The select option has a data source which is used to populate the select options (`categories`)
 */
export interface TransitionActionSelectOption {
  id: string;
  name: string;
  type: 'select' | 'multiSelect';
  dataSource: TransitionActionSelectOptionDataSource;
  categories?: BackendObjectRef[];
  default?: boolean;
}

export interface TransitionActionOptionRefundOptions {
    id: string;
    name: string;
    type: 'refund_options';
    isOptional: boolean;
    options?: TransitionActionOptionRefundOptionsOption[];
}

export interface TransitionActionOptionRefundOptionsOption {
  title: string;
  type: 'adjustment' | 'fixed' | 'custom';
  amount: number;
  metadata?: {[K: string]: string|number}[];
}

export interface TransitionActionFillInFieldsOptions {
  id: string;
  name: string;
  type: 'fill_in_fields';
  isOptional: boolean;
  options?: TransitionActionFillInFieldsOptionsOption[];
}

export interface TransitionActionFillInFieldsOptionsOption {
  id: string;
  required: boolean;
}

export const TransitionActionType = {
  applyUserFields:    'apply_userfields',
  changeOption:       'change_option',
  collectPayment:     'collect_payment',
  confirmBooking:     'confirm_booking',
  confirmSeat:        'confirm_seat',
  disconfirmBooking:  'disconfirm_booking',
  eligibilityCheck:   'eligibility_check',
  refundPayments:     'refund_payments',
  cancelPayments:     'cancel_payments',
  reserveSeat:        'reserve_seat',
  selectRefundOption: 'select_refund_option',
  sendEmail:          'send_email_to_owner',
  unsetSeat:          'unset_seat',
  fillInFields:       'fill_in_fields',
  assignRoles:        'assign_roles',
  removeRoles:        'remove_roles',
  setUserAsActive:    'set_user_as_active',
  setUserAsInactive:  'set_user_as_inactive',
  deleteBooking:      'delete_booking',
} as const;
type ValueOf<T> = T[keyof T];
type TransitionActionType = ValueOf<typeof TransitionActionType>;

export type TransitionActionOptionType = TransitionActionOption |
                                         TransitionActionSelectOption |
                                         TransitionActionOptionRefundOptions |
                                         TransitionActionFillInFieldsOptions;

function isTransitionActionSelectOption(option: TransitionActionOptionType): option is TransitionActionSelectOption {
  return (option as TransitionActionSelectOption).dataSource !== undefined &&
         (option.type === 'select' || option.type === 'multiSelect');
}

export class TransitionAction {
    private static ID_PREFIX = 'wta-';

    public static readonly AvailableActions: TransitionAction[] = [
        new TransitionAction(undefined,
                             $localize `Send e-mail to owner`,
                             TransitionActionType.sendEmail,
                             $localize `This action will send an email to the primary email address of the owner of the booking. The content of the email can be specified below.`,
                             [
                                 {id: 'subject', name: $localize `Subject`, type: 'text'},
                                 {id: 'html', name: $localize `Message (HTML)`, type: 'wysiwyg'}
                             ]),
        new TransitionAction(undefined,
                             $localize `Collect payment`,
                             TransitionActionType.collectPayment,
                             $localize `This action will request payment from the owner based upon the price and payment method set on their booking option`),
        new TransitionAction(undefined,
                              $localize `Cancel payment request`,
                              TransitionActionType.cancelPayments,
                              $localize `This action will cancel all payment requests`,
                              [
                              ]),
        new TransitionAction(undefined,
                             $localize `Select refund option`,
                             TransitionActionType.selectRefundOption,
                             $localize `This action will allow the user performing the transition to choose from a list of refund options provided below. This DOES NOT action the refund. To action a refund please choose the "refund payments" action.`,
                             [
                               {
                                 id: 'refund_options',
                                 name: $localize `Refund options`,
                                 type: 'refund_options',
                                 isOptional: false
                               },
                             ]),
        new TransitionAction(undefined,
                             $localize `Refund payments`,
                             TransitionActionType.refundPayments,
                             $localize `This action will trigger a refund to be generated for any transaction(s) processed on this booking. The type and amount of refund can be chosen by specifying refund options below.`,
                             [
                               {
                                 id: 'auto_refund',
                                 name: $localize `Immediately process the refund without requiring any further approval.`,
                                 type: 'boolean',
                                 default: true
                               },
                               {
                                 id: 'refund_options',
                                 name: $localize `Refund options`,
                                 type: 'refund_options',
                                 isOptional: false
                               },
                             ]),
        new TransitionAction(undefined,
                             $localize `Move this booking`,
                             TransitionActionType.changeOption,
                             $localize `This action will move this booking to a different booking option within the same booking item.`,
                             [
                               {
                                 id: 'change_only',
                                 name: $localize `Action the move to the preselected booking option. A booking option must have been preselected within a previous transition in the workflow for this to function.`,
                                 type: 'boolean'
                               },
                               {
                                 id: 'record_only',
                                 name: $localize `Preselect the chosen booking option. This DOES NOT action the move.`,
                                 type: 'boolean'
                               },
                               {
                                 id: 'handle_payments',
                                 name: $localize `Generate a refund and create a new payment request if the price of the chosen booking option differs from the current. This refund will require manual processing unless you choose to immediately process the refund below.`,
                                 type: 'boolean'
                               },
                               {
                                 id: 'auto_refund',
                                 name: $localize `Immediately process the refund without requiring any further approval.`,
                                 type: 'boolean'
                               },
                               {
                                 id: 'ignoreWindow',
                                 name: $localize `Allow this action to occur outside of the booking window for this booking option`,
                                 type: 'boolean'
                               },
                               {
                                 id: 'allowAnyOption',
                                 name: $localize `Allow a move to any booking option from within the same booking item. If this is not checked this booking can only be moved to another booking option within the same sub-hierarchy`,
                                 type: 'boolean'
                               },
                               {
                                 id: 'refund_options',
                                 name: $localize `Refund options`,
                                 type: 'refund_options',
                                 isOptional: true
                               },
                             ]),
        new TransitionAction(undefined,
                             $localize `Confirm booking`,
                             TransitionActionType.confirmBooking,
                             $localize `This action will mark this booking as confirmed. Confirmed bookings allow you to determine which bookings are complete.`),
        new TransitionAction(undefined,
                             $localize `Retract booking confirmation`,
                             TransitionActionType.disconfirmBooking,
                             $localize `This action will remove the confirmed booking marking.`),
        new TransitionAction(undefined,
                             $localize `Ensure user is still eligible`,
                             TransitionActionType.eligibilityCheck,
                             $localize `This action will evaluate the eligibility settings for the booking option and ensure the owner of the booking is still eligible at the point of transition.`),
        new TransitionAction(undefined,
                             $localize `Reserve seat`,
                             TransitionActionType.reserveSeat,
                             $localize `This action will reserve a seat for this booking for the amount of time specified below. Once the time expires this seat will be opened up.`,
                             [{id: 'ttl', name: $localize `Validity (in minutes)`, type: 'number'}]),
        new TransitionAction(undefined,
                             $localize `Confirm seat`,
                             TransitionActionType.confirmSeat,
                             $localize `This action will allocate a confirmed seat to this booking.`,
                             [{id: 'ttl', name: $localize `Validity (in minutes)`, type: 'number'}]),
        new TransitionAction(undefined,
                             $localize `Release seat`,
                             TransitionActionType.unsetSeat,
                             $localize `This action will remove any seat allocation for this booking and open the seat up.`),
        new TransitionAction(undefined,
                             $localize `Apply user fields to profile`,
                             TransitionActionType.applyUserFields,
                             $localize `This action will apply any changes to user fields made as part of the form to the user's profile.`),
        new TransitionAction(undefined,
                            $localize `Fill in fields`,
                            TransitionActionType.fillInFields,
                            $localize `This action will let you fill in fields during a transition`,
                            [
                                {id: 'fields', name: $localize `Fields`, type: 'fill_in_fields', isOptional: false},
                            ]),
        new TransitionAction(undefined,
                              $localize `Assign roles`,
                              TransitionActionType.assignRoles,
                              $localize `This action will assign the selected roles to the user associated with this booking.`,
                              [
                                {id: 'roles', name: $localize `Roles`, type: 'multiSelect', default: false, dataSource: 'availableRoles'},
                              ]),
        new TransitionAction(undefined,
                              $localize `Remove roles`,
                              TransitionActionType.removeRoles,
                              $localize `This action will remove the selected roles from the user associated with this booking.`,
                              [
                                {id: 'roles', name: $localize `Roles`, type: 'multiSelect', default: false, dataSource: 'availableRoles'},
                              ]),
        new TransitionAction(undefined,
                              $localize `Set user as active`,
                              TransitionActionType.setUserAsActive,
                              $localize `This action will set the user associated with this booking as active.`,
                              [
                                {id: 'roles', name: $localize `Roles`, type: 'multiSelect', default: false, dataSource: 'availableRoles'},
                              ]),
        new TransitionAction(undefined,
                              $localize `Set user as disabled`,
                              TransitionActionType.setUserAsInactive,
                              $localize `This action will set the user associated with this booking as disabled.`),
        new TransitionAction(undefined,
                              $localize `Delete booking`,
                              TransitionActionType.deleteBooking,
                              $localize `This action will delete the booking. This action is irreversible and will remove all data associated with the booking.`),
    ];

    public static get AvailableActionsByTypeMap(): {[K in TransitionActionType]: TransitionAction} {
      return TransitionAction.AvailableActions.reduce((result, action) => {
        result[action.type] = action;
        return result;
      }, {} as {[K in TransitionActionType]: TransitionAction});
    }

    public readonly description: string;
    public readonly id: string;
    public readonly type: TransitionActionType;
    public readonly name: string;
    public readonly options?: (TransitionActionOptionType)[];
    public          data?: TransitionActionData;

    public get isTemplate(): boolean {
        return !this.data;
    }

    public get hasOptions(): boolean {
        return !!this.options;
    }

    public constructor(id: string|undefined,
                       name: string,
                       type: TransitionActionType,
                       description?: string,
                       options?: (TransitionActionOptionType)[],
                       data?: TransitionActionData) {
    this.id = id ?? Utils.objectIdentifier(TransitionAction.ID_PREFIX);
        this.description = description ?? name;
        this.name = name;
        this.type = type;
        this.options = options;
        this.data = data;
    }

  /**
   * Transforms a Template into an Action
   *
   * @param data Configuration data from the User
   * @param id Optional ID to use otherwise new ID will be assigned
   * @returns Configured Action
   */
  public evaluateToAction(data: TransitionActionData, id?: string): TransitionAction {
    return new TransitionAction(id, this.name, this.type, this.description, this.options, data);
  }

  /**
   * Expands the template options from the provided data sources
   * 
   * The AvailableActions contains template definitions which may need additional
   * data to be injected into the options.
   * 
   * For example we may need to have a list of available roles to be injected into
   * the options for the AssignRoles action.
   * 
   * Template defines it's options and the type of widget (select, multiSelect) and
   * the `dataSource` attribute (see TransitionActionSelectOption). The `dataSource`
   * is then used to lookup the data source in the provided sources dictionary.
   *  
   * @param sources Dictionary of data sources used to populate the select options
   */
  public evaluateOptionCategoriesFromDataSource(sources: Record<TransitionActionSelectOptionDataSource,BackendObjectRef[]>): TransitionAction {
    let options = undefined;
    if (this.options !== undefined) {
      options = this.options.map(option => {
        if (isTransitionActionSelectOption(option)) {
            return {
              ...option,
              categories: sources[option.dataSource]
            };
        }
        return option;
      });
    }
    return new TransitionAction(undefined, this.name, this.type, this.description, options);
  }
}
